import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authAPI, albumAPI, adminAPI, publicAPI } from '../lib/api';

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [adminAuthed, setAdminAuthed] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [users, setUsers] = useState([]);
  const [booting, setBooting] = useState(true);
  const [publicPlan, setPublicPlan] = useState({});

  // Bootstrap: restore user + admin sessions via httpOnly cookies.
  // Falls back to legacy Authorization header (still sent by axios interceptor if
  // localStorage has an old token — hybrid mode).
  useEffect(() => {
    (async () => {
      // User session
      try {
        const me = await authAPI.me();
        setCurrentUser(me);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) {
          localStorage.removeItem('fl_token');
        } else if (status !== undefined) {
          console.warn('auth/me falhou:', status);
        }
      }
      // Admin session
      try {
        await adminAPI.verify();
        setAdminAuthed(true);
      } catch (e) {
        if (e?.response?.status === 401) {
          localStorage.removeItem('fl_adminToken');
        }
      }
      setBooting(false);
    })();
    // Load the public plan (used to render Pix, support WhatsApp, etc.).
    // Silently ignore failures — the UI falls back to hiding optional fields.
    publicAPI.plan().then(setPublicPlan).catch(() => {});
  }, []);

  const reloadPublicPlan = useCallback(async () => {
    try { setPublicPlan(await publicAPI.plan()); } catch {}
  }, []);

  // Load albums when user logs in
  const reloadAlbums = useCallback(async () => {
    if (!currentUser) { setAlbums([]); return; }
    try {
      const list = await albumAPI.listMine();
      setAlbums(list);
    } catch (e) {
      setAlbums([]);
    }
  }, [currentUser]);

  useEffect(() => { reloadAlbums(); }, [reloadAlbums]);

  // Load users when admin logs in
  const reloadUsers = useCallback(async () => {
    if (!adminAuthed) { setUsers([]); return; }
    try {
      const list = await adminAPI.listUsers();
      setUsers(list);
    } catch {
      setUsers([]);
    }
  }, [adminAuthed]);

  useEffect(() => { reloadUsers(); }, [reloadUsers]);

  // --- User auth ---
  const loginUser = async ({ email, password }) => {
    try {
      const { token, user } = await authAPI.login(email, password);
      localStorage.setItem('fl_token', token);
      setCurrentUser(user);
      return { ok: true, user };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || 'Erro ao entrar' };
    }
  };

  const logoutUser = async () => {
    await authAPI.logout();
    localStorage.removeItem('fl_token');
    setCurrentUser(null);
    setAlbums([]);
  };

  const updateSelf = async (patch) => {
    const updated = await authAPI.updateMe(patch);
    setCurrentUser(updated);
    return updated;
  };

  // --- Admin ---
  const loginAdmin = async (password) => {
    try {
      const { token } = await adminAPI.login(password);
      localStorage.setItem('fl_adminToken', token);
      setAdminAuthed(true);
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || 'Senha incorreta' };
    }
  };

  const logoutAdmin = async () => {
    await adminAPI.logout();
    localStorage.removeItem('fl_adminToken');
    setAdminAuthed(false);
    setUsers([]);
  };

  const changeAdminPassword = async (current, next) => {
    try {
      await adminAPI.changePassword(current, next);
      return { ok: true };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || 'Erro' };
    }
  };

  // --- Users (admin) ---
  const createUserByAdmin = async (data) => {
    try {
      const u = await adminAPI.createUser(data);
      setUsers(prev => [...prev, u]);
      return { ok: true, user: u };
    } catch (e) {
      return { ok: false, message: e?.response?.data?.detail || 'Erro' };
    }
  };

  const updateUserByAdmin = async (id, patch) => {
    const u = await adminAPI.updateUser(id, patch);
    setUsers(prev => prev.map(x => x.id === id ? u : x));
    return u;
  };

  const deleteUserByAdmin = async (id) => {
    await adminAPI.deleteUser(id);
    setUsers(prev => prev.filter(u => u.id !== id));
  };

  // --- Albums ---
  const createAlbum = async (data) => {
    const a = await albumAPI.create(data);
    setAlbums(prev => [a, ...prev]);
    return a;
  };

  const updateAlbum = async (code, patch) => {
    const a = await albumAPI.update(code, patch);
    setAlbums(prev => prev.map(x => x.code === code ? a : x));
    return a;
  };

  const deleteAlbum = async (code) => {
    await albumAPI.remove(code);
    setAlbums(prev => prev.filter(a => a.code !== code));
  };

  return (
    <AppContext.Provider value={{
      booting,
      currentUser, adminAuthed,
      albums, users,
      publicPlan, reloadPublicPlan,
      loginUser, logoutUser, updateSelf,
      loginAdmin, logoutAdmin, changeAdminPassword,
      createUserByAdmin, updateUserByAdmin, deleteUserByAdmin,
      createAlbum, updateAlbum, deleteAlbum,
      reloadAlbums, reloadUsers,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
};
